import React, { useState, useEffect, useCallback, useRef } from "react";
import { FiImage, FiX, FiDownload, FiShare2, FiUser } from "react-icons/fi";
import { photoService } from "../services/api";
import { Photo } from "../types";

interface PhotoGalleryProps {
  onPhotoUploaded?: () => void;
}

const PhotoSkeleton: React.FC = () => (
  <div className="aspect-square bg-gray-200 rounded-xl overflow-hidden relative animate-pulse">
    <div className="absolute inset-0 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 animate-shimmer"></div>
  </div>
);

const PhotoGallery: React.FC<PhotoGalleryProps> = ({ onPhotoUploaded }) => {
  const [photos, setPhotos] = useState<Photo[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedPhoto, setSelectedPhoto] = useState<Photo | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [offset, setOffset] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [isInitialized, setIsInitialized] = useState(false);

  const observer = useRef<IntersectionObserver>();
  const isRequesting = useRef(false);

  const fetchPhotos = async (isLoadMore: boolean = false) => {
    if (isRequesting.current) return;
    if (isLoadMore && (loadingMore || !hasMore)) return;

    isRequesting.current = true;

    if (isLoadMore) {
      setLoadingMore(true);
    } else {
      setLoading(true);
    }

    const response = await photoService.getPhotos(12, isLoadMore ? offset : 0);

    if (isLoadMore) {
      setPhotos((prev) => [...prev, ...response.photos]);
      setOffset(response.offset + response.limit);
    } else {
      setPhotos(response.photos);
      setOffset(response.limit);
    }

    setHasMore(response.has_more);
    setTotalCount(response.total_count);
    setError(null);

    setLoading(false);
    setLoadingMore(false);
    isRequesting.current = false;
  };

  const handleRefresh = async () => {
    if (refreshing || isRequesting.current) return;

    setRefreshing(true);
    setOffset(0);
    setHasMore(true);
    await fetchPhotos(false);
    setRefreshing(false);
  };

  const lastPhotoElementRef = useCallback(
    (node: HTMLDivElement) => {
      if (
        loading ||
        loadingMore ||
        !hasMore ||
        !isInitialized ||
        isRequesting.current
      )
        return;
      if (observer.current) observer.current.disconnect();

      observer.current = new IntersectionObserver((entries) => {
        if (
          entries[0].isIntersecting &&
          hasMore &&
          !loadingMore &&
          !isRequesting.current
        ) {
          fetchPhotos(true);
        }
      });

      if (node) observer.current.observe(node);
    },
    [loading, loadingMore, hasMore, isInitialized]
  );

  useEffect(() => {
    if (!isInitialized) {
      fetchPhotos(false);
      setIsInitialized(true);
    }
  }, []);

  useEffect(() => {
    if (onPhotoUploaded && isInitialized) {
      setOffset(0);
      setHasMore(true);
      fetchPhotos(false);
    }
  }, [onPhotoUploaded, isInitialized]);

  const handlePhotoClick = (photo: Photo) => {
    setSelectedPhoto(photo);
  };

  const closePhotoView = () => {
    setSelectedPhoto(null);
  };

  const handleDownload = async (photo: Photo) => {
    const response = await fetch(photo.url);
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = photo.filename;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  };

  const handleShare = async (photo: Photo) => {
    if (navigator.share) {
      await navigator.share({
        title: "Foto do Casamento",
        text: "Confira esta linda foto do casamento!",
        url: photo.url,
      });
    } else {
      await navigator.clipboard.writeText(photo.url);
    }
  };

  const renderSkeletonGrid = (count: number = 12) => (
    <div className="grid grid-cols-3 gap-2">
      {Array.from({ length: count }).map((_, index) => (
        <PhotoSkeleton key={index} />
      ))}
    </div>
  );

  if (loading && photos.length === 0) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="h-6 bg-gray-200 rounded-lg w-32 animate-pulse"></div>
          <div className="w-8 h-8 bg-gray-200 rounded-lg animate-pulse"></div>
        </div>

        {renderSkeletonGrid(12)}
      </div>
    );
  }

  if (error && photos.length === 0) {
    return (
      <div className="text-center py-12 px-4">
        <div className="space-y-4">
          <FiImage className="w-16 h-16 text-gray-300 mx-auto" />
          <p className="text-gray-600">{error}</p>
          <button onClick={handleRefresh} className="btn-primary">
            Tentar Novamente
          </button>
        </div>
      </div>
    );
  }

  if (photos.length === 0) {
    return (
      <div className="text-center py-12 px-4">
        <div className="space-y-4">
          <FiImage className="w-16 h-16 text-gray-300 mx-auto" />
          <h3 className="text-lg font-semibold text-gray-900">
            Nenhuma Foto Ainda
          </h3>
          <p className="text-gray-600">
            Seja o primeiro a compartilhar uma foto!
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-gray-900">
          Galeria ({totalCount})
        </h2>
        <button
          onClick={handleRefresh}
          disabled={refreshing}
          className="text-olive-green hover:text-olive-dark p-2"
        >
          <div className={`w-5 h-5 ${refreshing ? "animate-spin" : ""}`}>
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
              />
            </svg>
          </div>
        </button>
      </div>

      <div className="grid grid-cols-3 gap-2">
        {photos.map((photo, index) => {
          const isLastPhoto = index === photos.length - 1;

          return (
            <div
              key={photo.id}
              ref={isLastPhoto ? lastPhotoElementRef : null}
              onClick={() => handlePhotoClick(photo)}
              className="aspect-square bg-gray-100 rounded-xl overflow-hidden relative group cursor-pointer"
            >
              <img
                src={photo.thumbnail_url}
                alt={photo.filename}
                className="w-full h-full object-cover"
                loading="lazy"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.src = photo.url;
                }}
              />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all duration-200" />
            </div>
          );
        })}
      </div>

      {loadingMore && <div className="space-y-4">{renderSkeletonGrid(6)}</div>}

      {!hasMore && photos.length > 0 && (
        <div className="text-center py-6">
          <p className="text-sm text-gray-500">Todas as fotos carregadas</p>
        </div>
      )}

      {selectedPhoto && (
        <>
          <div className="backdrop" onClick={closePhotoView} />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="relative w-full h-full max-w-lg max-h-[80vh] bg-white rounded-3xl overflow-hidden">
              <button
                onClick={closePhotoView}
                className="absolute top-4 right-4 z-10 w-10 h-10 bg-black/20 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-black/30 transition-colors"
              >
                <FiX className="w-5 h-5" />
              </button>

              <div className="w-full h-full flex items-center justify-center p-4">
                <img
                  src={selectedPhoto.url}
                  alt={selectedPhoto.filename}
                  className="max-w-full max-h-full object-contain"
                  style={{
                    maxWidth: "100%",
                    maxHeight: "100%",
                    width: "auto",
                    height: "auto",
                  }}
                />
              </div>

              <div className="absolute bottom-4 left-4 right-4 flex items-center justify-center space-x-4">
                <button
                  onClick={() => handleDownload(selectedPhoto)}
                  className="w-12 h-12 bg-black/20 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-black/30 transition-colors"
                  title="Baixar foto"
                >
                  <FiDownload className="w-5 h-5" />
                </button>
                <button
                  onClick={() => handleShare(selectedPhoto)}
                  className="w-12 h-12 bg-black/20 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-black/30 transition-colors"
                  title="Compartilhar foto"
                >
                  <FiShare2 className="w-5 h-5" />
                </button>
              </div>

              {selectedPhoto.uploaded_by &&
                selectedPhoto.uploaded_by !== "Unknown" && (
                  <div className="absolute top-4 left-4 bg-black/20 backdrop-blur-sm rounded-full px-3 py-1">
                    <div className="flex items-center space-x-2">
                      <FiUser className="w-4 h-4 text-white" />
                      <span className="text-sm text-white font-medium">
                        {selectedPhoto.uploaded_by}
                      </span>
                    </div>
                  </div>
                )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default PhotoGallery;
